/**
 * The way into a patient's record, from a ward-list row or a search hit.
 *
 * **A `button`, not a click handler on the `tr`.** A row that is only clickable is
 * unreachable from a keyboard, has no accessible name, gets no focus ring and cannot be
 * found by a screen reader looking for what is actionable. On a ward tablet with a
 * keyboard attached — which is how a ward workstation is actually used — a clinician
 * tabs to a patient and presses Enter.
 *
 * **The visible text is the patient's name and the accessible name says what will
 * happen.** `Åkerlund, Karin` alone tells a screen-reader user nothing about where the
 * button goes; `Öppna patientöversikt för Åkerlund, Karin` contains the visible text, so
 * WCAG 2.5.3 (label in name) is satisfied rather than traded away.
 *
 * One component for both lists, for the reason `patientDisplay.ts` gives about the
 * badges: §2.2 and §2.3 are the two ways into a record (PHASE-0.md §3.4), and the
 * affordance must not be a link on one screen and a row click on the other.
 */

import type { Translate } from './patientDisplay'

export function OpenPatient({
  name,
  onOpen,
  t,
}: {
  /** The patient's name as the list renders it — already through `patientName`. */
  readonly name: string
  readonly onOpen: () => void
  readonly t: Translate
}) {
  return (
    <button
      type="button"
      className="record-open"
      aria-label={t('action.openPatient', { name })}
      onClick={onOpen}
    >
      {name}
    </button>
  )
}
